'use client';

import { useTranslations } from 'next-intl';
import React, { useMemo, useRef } from 'react';

import HighlightSectionItem from '@/app/[locale]/(default-layout)/_components/home-section-two/HighlightSectionItem';
import SectionTitle from '@/app/[locale]/(default-layout)/_components/SectionTitle';

import { useNewsletterTopics } from '@/components/newsletter/useNewsletterTopics';

import { ShowingDto } from '@cineamo/api/models/showing/ShowingDto.types';

import { styleFaded } from '@cineamo/ui/src/helpers/style-helper';

import useScrollState from '@cineamo/ui/src/hooks/useScrollState';

import BackAndForthButtonOverlay from '@cineamo/ui/src/button/BackAndForthButtonOverlay';
import { IconBell24 } from '@cineamo/ui/src/icons/ic-bell';
import { IconLoudBell24 } from '@cineamo/ui/src/icons/ic-loudBell.tsx';

import CenterLayout from '@cineamo/composables/src/CenterLayout';

import { useNewsletterModal } from '@/providers/NewsletterModalProvider';

type HighlightsTabPanelProps = {
    highlights: ShowingDto[];
};

// Normalize a highlight text for grouping/equality: unify Unicode composition, drop zero-width
// characters, collapse any run of whitespace (incl. non-breaking spaces) to a single space, trim,
// and lowercase — so visually-identical texts that differ only by invisible characters or casing
// still group together.
function normalizeHighlightText(text?: string | null): string {
    return (text ?? '')
        .normalize('NFC')
        .replace(/[\u200B-\u200D\uFEFF]/g, '')
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase();
}

const HighlightsTabPanel: React.FC<HighlightsTabPanelProps> = ({ highlights }) => {
    const eventsScrollContainerRef = useRef(null);
    const t = useTranslations();
    const { newsletterEnabled, subscriberStatus, openModal } = useNewsletterModal();
    const { allTopics } = useNewsletterTopics();

    // Hidden when the operator has removed the "highlights" newsletter section — otherwise this
    // offers a subscription whose toggle the modal filters out of the form.
    const isHighlightTopicAvailable = useMemo(() => allTopics.some((topic) => topic.type === 'highlight'), [allTopics]);

    const { styleFadedState } = useScrollState({
        scrollContainerRef: eventsScrollContainerRef,
        initialState: {
            startReached: true,
            endReached: false
        }
    });

    // Group highlighted showings by movie (content) AND highlight text: showings only land in the
    // same card when both the title (content) and the (normalized) highlight text match, so
    // differing highlight texts for the same film produce separate cards. Showings sorted by date,
    // groups ordered by their earliest showing.
    const groupedHighlights = useMemo(() => {
        const groups = new Map<string, ShowingDto[]>();
        (highlights ?? []).forEach((showing) => {
            const groupKey = `${showing.contentId}${normalizeHighlightText(showing.highlightText)}`;
            const group = groups.get(groupKey) ?? [];
            group.push(showing);
            groups.set(groupKey, group);
        });
        return Array.from(groups.values())
            .map((group) =>
                [...group].sort((a, b) => new Date(a.startDatetime).getTime() - new Date(b.startDatetime).getTime())
            )
            .sort((a, b) => new Date(a[0].startDatetime).getTime() - new Date(b[0].startDatetime).getTime());
    }, [highlights]);

    return (
        <CenterLayout className="space-y-12 lg:space-y-16">
            <div className="flex items-center justify-between">
                <SectionTitle title={t('home.highlights')} />
                {newsletterEnabled &&
                    isHighlightTopicAvailable &&
                    (() => {
                        const isSubscribed =
                            subscriberStatus?.subscribed &&
                            subscriberStatus.topics?.some((sub) => sub.type === 'highlight' && !sub.referenceId);
                        if (isSubscribed) {
                            return (
                                <span className="flex items-center gap-6 text-body-bold-12 text-[var(--primary-color)]">
                                    <IconLoudBell24 className="size-18" />
                                    {t('newsletter-modal.subscribed')}
                                </span>
                            );
                        }
                        return (
                            // Opens the modal pre-selecting only the section-wide 'highlight' topic
                            // (CIN-13995) — the same topic the isSubscribed check above reads. It
                            // used to link to /newsletter, which pre-selected everything.
                            <button
                                type="button"
                                onClick={() => openModal([{ type: 'highlight', label: t('home.highlights') }])}
                                className="flex items-center gap-6 text-body-bold-12 hover:opacity-80 transition-opacity text-[var(--primary-color)]">
                                <IconBell24 className="size-18" />
                                {t('newsletter-modal.subscribe')}
                            </button>
                        );
                    })()}
            </div>
            <div className="relative flex flex-row">
                <div
                    className="relative w-full flex flex-row items-start space-x-16 overflow-x-auto no-scrollbar"
                    style={styleFaded(styleFadedState, 5)}
                    ref={eventsScrollContainerRef}>
                    {groupedHighlights.map((group) => (
                        <HighlightSectionItem
                            key={`HighlightSectionItem#${group[0].contentId}#${normalizeHighlightText(group[0].highlightText)}`}
                            showings={group}
                        />
                    ))}
                </div>
                {groupedHighlights.length > 0 && (
                    <BackAndForthButtonOverlay
                        scrollContainerRef={eventsScrollContainerRef}
                        renderOutside
                    />
                )}
            </div>
        </CenterLayout>
    );
};

export default HighlightsTabPanel;
